import { useState } from 'react';
import { imagePickSize } from 'utils/general';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Avatar from 'components/Avatar';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import IconSend from 'assets/images/calendar/icon-send-white.svg';
import CommentOptions from './CommentOptions';

import './styles.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const TaskComments = ({ comments, addComment, editComment, deleteComment, timeZone, userId }) => {
  const [newComment, setNewComment] = useState('');
  const [editModeId, setEditModeId] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [tooltipMessage, setTooltipMessage] = useState('');

  const { textareaRef } = useAutosizeTextarea({ extraHeight: 30 });

  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(tooltipMessage, { position: 'bottom' });

  const handleSubmit = () => {
    const trimmed = newComment.trim();
    if (trimmed) {
      addComment(trimmed);
      setNewComment('');
    }
  };

  const handleEdit = (comment) => {
    setEditModeId(comment.id);
    setEditedText(comment.body);
  };

  const handleSave = async (commentId, originalBody) => {
    const trimmed = editedText.trim();
    if (!trimmed) return;

    if (trimmed === originalBody.trim()) {
      setEditModeId(null);
      setEditedText('');
      return;
    }

    await editComment(commentId, trimmed);
    setEditModeId(null);
    setEditedText('');
  };

  const handleCancel = () => {
    setEditModeId(null);
    setEditedText('');
  };

  return (
    <>
      <div className="comment-container">
        <label htmlFor="comments">comments</label>
        <div className="comments__textarea-wrapper">
          <textarea
            ref={textareaRef}
            name="comments"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment"
          />
          <button
            className="comments__submit button"
            onClick={handleSubmit}
          >
            Submit
            <img
              src={IconSend}
              className="comments__submit-icon"
            />
          </button>
        </div>
      </div>
      <div className="comments-list">
        {comments
          .sort((a, b) => dayjs(b.created_at) - dayjs(a.created_at))
          .map(comment => {
          const { profile_image, firstname, lastname, initials } = comment.login;
          const profileImage = profile_image ?? null;
          let imagePath = null;
          if (profileImage) {
            imagePath = `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(
              profileImage,
              'square60',
            )}`;
          }

          const isEditing = editModeId === comment.id;

          return (
            <div
              key={`comment-${comment.id}`}
              className="task-comment"
            >
              <div className="task-comment__image">
                <Avatar
                  photo={imagePath}
                  text={!imagePath ? initials : null}
                  size={24}
                  borderSize={1}
                />
              </div>
              <div className="task-comment__content">
                <div className="task-comment__head">
                  <div className="task-comment__name">
                    {firstname} {lastname}
                  </div>
                  <div
                    className="task-comment__date"
                    onMouseEnter={e => {
                      const date = dayjs(comment.created_at ?? '')
                        .tz(timeZone)
                        .format('MMM D, YYYY, h:mma');
                      showTooltip();
                      updateTooltip(e);
                      setTooltipMessage(date);
                    }}
                    onMouseMove={e => updateTooltip(e)}
                    onMouseLeave={hideTooltip}
                  >
                    {dayjs(comment.created_at).format('MMM D')}
                    {tooltipElement}
                  </div>
                  {comment.login_id === userId ? (
                    <div className="task-comment__options">
                      <CommentOptions
                        commentId={comment.id}
                        onEdit={() => handleEdit(comment)}
                        onDelete={() => deleteComment(comment.id)}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="task-comment__body">
                  {isEditing ? (
                    <>
                      <textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                      />
                      <div className="task-comment__edit-buttons">
                        <button
                          onClick={() => handleSave(comment.id, comment.body)}
                          className="button button--md"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="button button--outline button--md"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    comment.body
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TaskComments;
